/**
 * PasskeySettingsCard — manage Face ID / Touch ID / Windows Hello passkeys.
 *
 * Drops into `/profile`. Lets the user enroll a passkey on the current
 * device and revoke any device they previously enrolled. The biometric
 * prompt is fired by `useAuth().registerPasskey()` which wraps
 * `@simplewebauthn/browser`'s `startRegistration` — the OS handles the
 * actual Face ID / Touch ID UX, no extra config required.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Fingerprint, Trash2, ShieldCheck, Plus, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

export const PasskeySettingsCard = () => {
  const { registerPasskey } = useAuth();
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);
  const [supported, setSupported] = useState(true);

  const fetchCreds = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/webauthn/credentials`, { withCredentials: true });
      setCreds(data.credentials || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Browser support check (lazy-loaded so the lib only ships when needed)
    import('@simplewebauthn/browser').then(m => setSupported(m.browserSupportsWebAuthn()));
    fetchCreds();
  }, [fetchCreds]);

  const handleEnroll = async () => {
    setErr(null); setSuccess(null); setEnrolling(true);
    const res = await registerPasskey();
    setEnrolling(false);
    if (res.success) {
      setSuccess(`Passkey added · ${res.credential?.device_label || 'this device'}`);
      fetchCreds();
    } else {
      setErr(res.error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this passkey? You will need to re-enroll on this device to use Face ID again.')) return;
    try {
      await axios.delete(`${API_URL}/api/webauthn/credentials/${id}`, { withCredentials: true });
      fetchCreds();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <Card className="border-emerald-500/30 bg-emerald-500/[0.03]" data-testid="passkey-settings-card">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Fingerprint className="w-5 h-5 text-emerald-500" />
            <h3 className="text-base font-semibold">Biometric Quick Unlock</h3>
          </div>
          {creds.length > 0 && (
            <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-600 bg-emerald-500/10 border border-emerald-500/40 rounded-full px-2 py-0.5">
              Active
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
          Sign in with Face ID, Touch ID, or Windows Hello on devices you trust.
          Your password stays as the always-available backup.
        </p>

        {!supported && (
          <div className="text-xs text-amber-600 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5 mb-3">
            This browser doesn't support passkeys. Try Safari (iOS/macOS),
            Chrome, or Edge on a device with biometrics.
          </div>
        )}

        {err && (
          <div className="text-xs text-rose-600 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2.5 mb-3" data-testid="passkey-error">
            {err}
          </div>
        )}
        {success && (
          <div className="text-xs text-emerald-600 dark:text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5 mb-3" data-testid="passkey-success">
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />{success}
          </div>
        )}

        {/* Enroll button */}
        {supported && (
          <Button
            data-testid="passkey-enroll-btn"
            onClick={handleEnroll}
            disabled={enrolling}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {enrolling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Prompting…</> :
             <><Plus className="w-4 h-4 mr-2" />Add Passkey on this device</>}
          </Button>
        )}

        {/* Existing credentials */}
        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="text-xs text-muted-foreground">Loading enrolled devices…</div>
          ) : creds.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No passkeys yet. Add one above to start.</div>
          ) : (
            creds.map((c) => (
              <div key={c.id}
                   data-testid={`passkey-row-${c.id}`}
                   className="flex items-center justify-between bg-background border border-border rounded-lg px-3 py-2">
                <div>
                  <div className="text-sm font-medium">{c.device_label || 'Unnamed device'}</div>
                  <div className="text-[10px] text-muted-foreground">
                    Added {formatDate(c.created_at)}
                    {c.last_used_at && <> · Last used {formatDate(c.last_used_at)}</>}
                  </div>
                </div>
                <Button
                  variant="ghost" size="icon"
                  onClick={() => handleDelete(c.id)}
                  data-testid={`passkey-delete-${c.id}`}
                  className="text-rose-500 hover:text-rose-600 hover:bg-rose-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};
